import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DeleteOutline, Refresh } from '@mui/icons-material';
import { collection, deleteDoc, doc, getDocs, getFirestore } from 'firebase/firestore';
import { apiFetch } from '../config/api';
import { isDressLinkedToAccount } from '../utils/dressOwnerUtils';

type OrphanRow = {
  id: string;
  title: string;
  price: number;
  location: string;
  reason: string;
  ownerUid?: string;
};

const REASON_LABELS: Record<string, string> = {
  invalid_or_missing_uid: 'UID propriétaire invalide ou absent',
  user_doc_missing: 'Aucun document users/{uid}',
  user_profile_incomplete: 'Compte incomplet (pas d’email / profil)',
  invalid_listing_document: 'Annonce invalide (pas créée via l’app : createdAt / images)',
  blocked_listing: 'Annonce bloquée (retrait manuel)',
};

const OrphanDressCleanup: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [orphans, setOrphans] = useState<OrphanRow[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadOrphans = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, 'dresses'));
      const rows: OrphanRow[] = [];

      for (const d of snap.docs) {
        const data = d.data() as Record<string, unknown>;
        const link = await isDressLinkedToAccount(db, data);
        if (!link.valid) {
          rows.push({
            id: d.id,
            title: (data.title as string) || (data.name as string) || 'Sans titre',
            price: (data.price as number) || 0,
            location: (data.location as string) || '—',
            reason: link.reason || 'unknown',
            ownerUid: link.ownerUid,
          });
        }
      }

      setOrphans(rows);
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'Impossible de charger les annonces.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrphans();
  }, [loadOrphans]);

  const handleDeleteAll = async () => {
    if (orphans.length === 0) return;
    const ok = window.confirm(
      `Supprimer définitivement ${orphans.length} annonce(s) non rattachée(s) à un compte valide ?`
    );
    if (!ok) return;

    setDeleting(true);
    setMessage(null);
    try {
      // 1) Suppression serveur (Firebase Admin sur Vercel) si disponible
      try {
        const apiRes = await apiFetch('/api/admin/cleanup-orphan-dresses', { method: 'DELETE' });
        const apiData = await apiRes.json().catch(() => ({}));
        if (apiRes.ok && apiData?.success) {
          setMessage({
            type: 'success',
            text: `${apiData.deleted ?? orphans.length} annonce(s) supprimée(s) via le serveur.`,
          });
          await loadOrphans();
          return;
        }
      } catch {
        // Fallback client ci-dessous
      }

      // 2) Fallback : suppression directe Firestore (admin + règles déployées)
      const db = getFirestore();
      await Promise.all(orphans.map((o) => deleteDoc(doc(db, 'dresses', o.id))));
      setMessage({
        type: 'success',
        text: `${orphans.length} annonce(s) supprimée(s).`,
      });
      await loadOrphans();
    } catch (e: unknown) {
      const err = e as { message?: string };
      setMessage({
        type: 'error',
        text:
          err?.message ||
          'Échec de la suppression. Vérifiez isAdmin dans Firestore, déployez firestore.rules, ou configurez REACT_APP_API_URL.',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Annonces sans compte valide
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Une robe doit être liée à un compte avec email ou profil. Les annonces ci-dessous seront masquées
            du catalogue et peuvent être supprimées ici.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button startIcon={<Refresh />} onClick={loadOrphans} disabled={loading || deleting}>
            Actualiser
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteOutline />}
            onClick={handleDeleteAll}
            disabled={loading || deleting || orphans.length === 0}
          >
            {deleting ? 'Suppression…' : `Supprimer tout (${orphans.length})`}
          </Button>
        </Box>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : orphans.length === 0 ? (
        <Alert severity="success">Aucune annonce orpheline détectée.</Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Titre</TableCell>
                <TableCell>Prix</TableCell>
                <TableCell>Lieu</TableCell>
                <TableCell>Raison</TableCell>
                <TableCell>UID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orphans.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>{row.price} €</TableCell>
                  <TableCell>{row.location}</TableCell>
                  <TableCell>{REASON_LABELS[row.reason] || row.reason}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {row.ownerUid || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default OrphanDressCleanup;
